import { useEffect, useState } from "react";
import { Switch } from "@headlessui/react";
import classNames from "classnames";
import { Controller } from "react-hook-form";

const Inner = ({
  checked,
  setEnabled,
  disabled,
  name,
}: {
  checked: boolean;
  disabled?: boolean;
  name?: string;
  setEnabled: (enabled: boolean) => void;
}) => (
  <Switch
    disabled={disabled}
    checked={checked}
    onChange={setEnabled}
    data-test-id={name && `toggle-${name}`}
    className={classNames(
      "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full",
      "cursor-pointer transition-colors ease-in-out duration-200",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
      {
        "bg-light": checked,
        "bg-grey-light": !checked,
        "cursor-pointer": !disabled,
        "bg-grey-lightest cursor-default": disabled,
      },
    )}
  >
    <span className="sr-only">Use setting</span>
    <span
      aria-hidden="true"
      className={classNames(
        "pointer-events-none inline-block h-5 w-5 rounded-full",
        "bg-white transform ring-0 transition ease-in-out duration-200",
        {
          "translate-x-5": checked,
          "translate-x-0": !checked,
          "bg-disabled-grey cursor-default": disabled,
        },
      )}
    />
  </Switch>
);

const Toggle = ({
  control,
  name,
  onChange,
  value = false,
  effectValue,
  disabled,
}: {
  control?: any;
  name?: string;
  value?: boolean;
  effectValue?: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}) => {
  const [innerEnabled, setInnerEnabled] = useState(value);

  useEffect(() => {
    /* 
      Sync inner state with effectValue prop when not controlled by useForm 
      control, particularly when setValue is used externally.
    */
    if (
      !control &&
      typeof effectValue === "boolean" &&
      effectValue !== innerEnabled
    ) {
      setInnerEnabled(effectValue);
    }
  }, [effectValue, innerEnabled, control]);

  if (control) {
    return (
      <Controller
        control={control}
        name={name!}
        render={({ field }) => (
          <Inner
            disabled={disabled}
            checked={field.value}
            name={name}
            setEnabled={(value) => {
              field.onChange(value);
              if (onChange) {
                onChange(value);
              }
            }}
          />
        )}
      />
    );
  }

  return (
    <Inner
      disabled={disabled}
      checked={innerEnabled}
      setEnabled={(value) => {
        if (onChange) {
          onChange(value);
        }
        setInnerEnabled(value);
      }}
    />
  );
};

export default Toggle;
